import { useMemo } from 'react';
import {
	duplicateFlowSignatureSlots,
	flowSignatureSlots,
	headerRowFlowSignatureSlots,
} from '@dealstack/shared/lib/common/business/documentFlow';
import { signatureSlotKey } from '@dealstack/shared/lib/common/business/signatureSlotKey';
import type { SignatureSlotTuple } from '@dealstack/shared/lib/common/business/signatureSlotKey';
import type { DocumentFlowDoc } from '@dealstack/shared/lib/common/business/documentFlow';
import type { SignatureSlotDisplay } from '@dealstack/frontend/shared/components/DocumentEditor/extensions/signatureNode';
import type { SignatureSlotListItem } from '@dealstack/frontend/shared/components/DocumentEditor/flowDocumentEditor.types';
import type { SignatureSlotRes } from '@dealstack/shared/lib/generated';
import type { DocxSignatureSlotsResult } from '@dealstack/frontend/shared/components/FieldBinding/DocxFieldBinding/docxFlowEditor.adapter.ts';

/**
 * The signatures a Word document has to carry, and how far the author has got.
 *
 * Takes the slots rather than fetching them: a deal's master document and a programme's KYC
 * form read them from different endpoints and everything downstream is identical.
 *
 * The slot LIST comes from the template's signing roles (`enumerateSlots`, via the same endpoint
 * the PDF placement editor uses), never from the document — so a role added after the document
 * was written shows up as unplaced instead of being silently absent. Which of them are PLACED
 * comes from the document, through the same walker the save and the renderer use.
 *
 * `document` is the edited document when there is one, so the counts follow an anchor inserted
 * or deleted a keystroke ago rather than what is stored.
 */
export const useDocxSignatureSlotsFrom = ({
	slots,
	isPending,
	isError,
	document,
}: {
	/** Undefined while unresolved or failed — see `isSlotsUnknown`. */
	slots: SignatureSlotRes[] | undefined;
	isPending: boolean;
	isError: boolean;
	document: DocumentFlowDoc | null;
}): DocxSignatureSlotsResult => {
	/**
	 * Until the slots are known, nothing here can be trusted.
	 *
	 * An unresolved (or failed) query leaves `slots` undefined, which read as "no slots" — so
	 * `isSignable` was false, `unplacedCount` 0 and the document vacuously complete. A slow or
	 * 500ing endpoint therefore let a half-anchored document save, and the derived
	 * `useManualSignaturePlacement` then sent signing down the manual path where the UNPLACED
	 * signer's prepare throws on their own document. Blocked instead.
	 */
	const isSlotsUnknown = isPending || isError || slots === undefined;

	const signatureSlotList = useMemo<SignatureSlotListItem[]>(
		() =>
			(slots ?? []).map((slot) => ({
				signingRoleId: slot.signingRoleId,
				isWitness: slot.isWitness,
				signatureIndex: slot.signatureIndex,
				label: slot.label,
			})),
		[slots]
	);

	const signatureSlotDisplay = useMemo<Record<string, SignatureSlotDisplay>>(
		() =>
			Object.fromEntries(
				(slots ?? []).map((slot) => [
					signatureSlotKey(slot),
					{ label: slot.label, roleType: slot.roleType },
				])
			),
		[slots]
	);

	const { placedKeys, anchorCount } = useMemo(() => {
		const anchors = document === null ? [] : flowSignatureSlots(document);
		return {
			placedKeys: new Set(anchors.map((slot) => signatureSlotKey(slot))),
			// Every anchor the document holds, not just the ones the current roles describe —
			// the same count the server's `if (anchors.length > 0)` gate reads, so an anchor for
			// a since-deleted role keeps this out of the "places nothing" case there and here.
			anchorCount: anchors.length,
		};
	}, [document]);

	const unplacedCount = signatureSlotList.filter(
		(slot) => !placedKeys.has(signatureSlotKey(slot))
	).length;

	/**
	 * A slot's label, for EVERY key in the tree — not just the ones the current roles describe.
	 *
	 * The save refuses a bad anchor for any key it finds, so filtering to known slots hid the
	 * case where a role was deleted after its anchors were placed: Save enabled, alert hidden,
	 * 400 on submit with nothing on screen to act on.
	 */
	const labelForSlot = useMemo(() => {
		const labelByKey = new Map(
			signatureSlotList.map((slot) => [signatureSlotKey(slot), slot.label])
		);
		return (slot: SignatureSlotTuple) => {
			const key = signatureSlotKey(slot);
			return labelByKey.get(key) ?? `A signature for a role that no longer exists (${key})`;
		};
	}, [signatureSlotList]);

	/**
	 * Slots anchored more than once, which the save refuses.
	 *
	 * Counted separately because `placedKeys` is a SET: two anchors for one slot collapse to one
	 * key, so a duplicate reads as "placed" and completeness alone would enable Save on a
	 * document the backend rejects with a 400. An anchor is a copy-pasteable atom, so the palette
	 * refusing a second drag does not prevent it.
	 */
	const duplicatedLabels = useMemo(
		() => (document === null ? [] : duplicateFlowSignatureSlots(document).map(labelForSlot)),
		[document, labelForSlot]
	);

	/**
	 * Anchors in a table header row, which the save refuses for a reason nothing else here
	 * covers: the row repeats on every continuation page, so it reserves the stamp's height on
	 * all of them while only one stamp is drawn.
	 *
	 * Mirrored client-side because the anchor is VISIBLE — it is sitting in a row the author can
	 * drag it out of. Left to the server it was a 400 on a Save that looked ready, which is how
	 * a Word document whose execution block happens to start with a repeating header row became
	 * one that "cannot be saved" while the one beside it saved fine.
	 */
	const headerRowLabels = useMemo(
		() => (document === null ? [] : headerRowFlowSignatureSlots(document).map(labelForSlot)),
		[document, labelForSlot]
	);

	return {
		isSignable: signatureSlotList.length > 0,
		signatureSlotList,
		signatureSlotDisplay,
		unplacedCount,
		isPartiallyPlaced: anchorCount > 0 && unplacedCount > 0,
		duplicatedLabels,
		headerRowLabels,
		isSlotsUnknown,
		/**
		 * All of them, or none of them — the server's rule, mirrored.
		 *
		 * `saveDocument` only calls `assertEverySlotAnchored` when the document holds at least
		 * one anchor, because a document that places NO signatures is the automatic-signatures-
		 * page case and is perfectly valid. Demanding completeness unconditionally disabled Save
		 * on every Word template that has signing roles and no anchors — which is every existing
		 * one, so an ordinary binding edit could not be saved — and made "remove the last anchor
		 * to go back to the automatic page" unreachable, since the result was incomplete.
		 *
		 * Still false while the slots are UNKNOWN, which looks identical from here and is not
		 * safe to treat either way.
		 */
		isComplete:
			!isSlotsUnknown &&
			(anchorCount === 0 || unplacedCount === 0) &&
			duplicatedLabels.length === 0 &&
			headerRowLabels.length === 0,
	};
};
